import React from "react";
import { Plus } from "lucide-react";

interface OrderSidebarProps {
  totalMRPPrice: () => number;
  calculateTotalDiscount: () => number;
  roundOffValue: number;
  existingCoins: number;
  showDiscountInput: boolean;
  setShowDiscountInput: (val: boolean) => void;
  discountValue: string;
  setDiscountValue: (val: string) => void;
  setExtraDiscount: (val: number) => void;
  formData: any;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  calculateFinalAmount: () => number;
  handlePreview: () => void;
  setShowModal: (val: boolean) => void;
  coinsused: number | "";
  setCoinsused: (val: number | "") => void;
}

const OrderSidebar: React.FC<OrderSidebarProps> = ({
  totalMRPPrice,
  calculateTotalDiscount,
  roundOffValue,
  existingCoins,
  showDiscountInput,
  setShowDiscountInput,
  discountValue,
  setDiscountValue,
  setExtraDiscount,
  formData,
  handleInputChange,
  calculateFinalAmount,
  handlePreview,
  setShowModal,
  coinsused,
  setCoinsused,
}) => {
  const [showCoinsInput, setShowCoinsInput] = React.useState(false);

  const handleCoinsInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    console.log(val)
    if (val === "") {
      setCoinsused("");
      return;
    }
    const num = parseInt(val, 10);
    if (!isNaN(num) && num >= 0) {
      if (num > existingCoins) {
        setCoinsused(existingCoins);
      } else {
        setCoinsused(num);
      }
    }
  };
  return (
    <div className="w-[300px] flex flex-col gap-4 flex-shrink-0">
      {/* Add Medicines Button */}
      <button
        id="add-medicines-btn"
        onClick={() => setShowModal(true)}
        className="relative w-full overflow-hidden flex items-center rounded-xl transition-transform hover:scale-[1.02] active:scale-[0.98] border border-1 border-[#199C4D]"
        style={{ height: "70px" }}
      >
        {/* Left white section */}
        <div className="flex items-center px-5 bg-white w-[220px] h-full">
          <span className="text-gray-800 font-semibold text-sm whitespace-nowrap">
            Add Medicines
          </span>
        </div>

        {/* Diagonal divider + green right section */}
        <div
          className="relative flex items-center justify-center h-full"
          style={{ backgroundColor: "#199C4D", width: "80px" }}
        >
          {/* Angled left edge overlay */}
          <div
            className="absolute left-0 top-0 h-full w-4 bg-white"
            style={{
              clipPath: "polygon(0 0, 100% 0, 60% 100%, 0 100%)",
            }}
          />
          <Plus size={20} color="white" className="relative z-10" />
        </div>
      </button>

      {/* Coins Row */}
      <div
        className="rounded-lg px-4 py-3 flex items-center justify-between"
        style={{ backgroundColor: "#E8E8E8" }}
      >
        <div className="flex flex-col">
          <span className="text-sm text-gray-800">
            Coins in wallet: <b>₹ {existingCoins}</b>
          </span>
          {coinsused !== "" && coinsused > 0 && (
            <span className="text-xs text-green-700 mt-0.5">
              - ₹ {coinsused} used
            </span>
          )}
        </div>

        {showCoinsInput ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={coinsused}
              onChange={handleCoinsInput}
              placeholder="0"
              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
            />
            <button
              onClick={() => setShowCoinsInput(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Done
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowCoinsInput(true)}
            className="text-xs bg-white border border-gray-300 rounded px-3 py-1.5 text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
          >
            Add Coins
          </button>
        )}
      </div>

      {/* Totals */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Total MRP</span>
          <span className="font-semibold text-gray-800">₹ {totalMRPPrice().toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Total Discount</span>
          <span className="font-semibold text-green-600">
            ₹ {(calculateTotalDiscount() + (parseFloat(discountValue) || 0)).toFixed(2)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Round Off</span>
          <span className="font-semibold text-green-600">₹ {roundOffValue}</span>
        </div>

        {/* Additional Discount */}
        {showDiscountInput ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={discountValue}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^(\d+\.?\d*|\.\d+)$/.test(v)) {
                  setDiscountValue(v);
                  setExtraDiscount(parseFloat(v) || 0);
                }
              }}
              className="border rounded px-2 py-1 text-sm w-24 focus:outline-none focus:ring-1 focus:ring-green-500"
              placeholder="0.00"
            />
            <button
              onClick={() => {
                setShowDiscountInput(false);
                setDiscountValue("");
                setExtraDiscount(0);
              }}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowDiscountInput(true)}
            className="text-sm font-medium"
            style={{ color: "#8ECB9E" }}
          >
            + Add additional Discount
          </button>
        )}

        {/* Payment Type inside Totals card matching UI */}
        <div
          className="rounded-xl p-4 text-white mt-4"
          style={{ backgroundColor: "#781D51" }} // Specific color from user
        >
          <div className="flex items-center justify-between mb-3">
            <span className="font-semibold text-sm">Payment Type</span>
          </div>
          <div className="flex gap-4">
            {["Cash", "Online", "Split"].map((type) => {
              const isSelected = formData.paymentType === type;
              return (
                <label key={type} className="flex items-center gap-2 cursor-pointer select-none">
                  <span className="relative flex items-center justify-center">
                    <input
                      type="radio"
                      name="paymentType"
                      value={type}
                      checked={isSelected}
                      onChange={handleInputChange}
                      style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                    />
                    <span
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${isSelected
                        ? "border-green-700 bg-green-700"
                        : "border-white/50 bg-transparent"
                        }`}
                    >
                      {isSelected && (
                        <span className="w-1.5 h-1.5 rounded-full bg-white" />
                      )}
                    </span>
                  </span>
                  <span
                    className={`text-sm transition-colors ${isSelected
                      ? "text-white font-semibold"
                      : "text-white/80"
                      }`}
                  >
                    {type}
                  </span>
                </label>
              );
            })}
          </div>

          {/* Dynamic input fields based on Split or default Cash/Online matching UI*/}
          {formData.paymentType === "Split" ? (
            <div className="mt-4 space-y-3">
              <input
                type="number"
                name="cashAmount"
                placeholder="Amount Paid in Cash"
                value={formData.cashAmount || ""}
                onChange={handleInputChange}
                className="w-full px-3 py-2 rounded bg-white text-gray-800 text-sm focus:outline-none"
              />
              <div className="text-sm text-white/90 px-1 pt-1 opacity-90">
                Amount Paid Online: ₹{Math.max(0, calculateFinalAmount() - (formData.cashAmount || 0)).toFixed(0)}
              </div>
            </div>
          ) : formData.paymentType === "Online" ? (
            <div className="mt-4">
              <div className="px-1 py-1 text-sm text-white/90 opacity-90">
                Amount Paid Online: ₹{calculateFinalAmount().toFixed(0)}
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <div className="px-1 py-1 text-sm text-white/90 opacity-90">
                Amount Paid in Cash: ₹{calculateFinalAmount().toFixed(0)}
              </div>
            </div>
          )}
        </div>

        {/* Total Payable + Preview */}
        <div className="pt-2">
          <div className="text-sm text-gray-500 mb-1">Total Payable</div>
          <div className="flex items-center justify-between">
            <span className="text-xl font-bold text-green-700">
              ₹ {calculateFinalAmount().toFixed(0)}
            </span>
            <button
              onClick={handlePreview}
              className="px-6 py-2 rounded-lg text-white font-semibold text-sm transition-all hover:bg-opacity-90 shadow-sm"
              style={{ backgroundColor: "#199C4D" }}
            >
              Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSidebar;
